import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';
import { n8nService } from '@/services/workflow/n8nService';

export interface WorkflowRule {
  id: string;
  name: string;
  description: string;
  trigger_type:
    | 'contact_created'
    | 'opportunity_created'
    | 'deal_won'
    | 'deal_lost'
    | 'activity_logged'
    | 'email_received'
    | 'time_based';
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
  is_active: boolean;
  priority: number;
  created_by: string;
  created_at: string;
  last_triggered?: string;
  execution_count: number;
  n8n_workflow_id?: string; // Link to n8n workflow
  use_n8n?: boolean; // Flag to use n8n instead of built-in engine
  environment?: 'test' | 'live';
  status?: 'idle' | 'running' | 'success' | 'error';
}

// Union type for workflow values
export type WorkflowValue = string | number | boolean | Date | null;

export interface WorkflowCondition {
  field: string;
  operator:
    | 'equals'
    | 'not_equals'
    | 'contains'
    | 'not_contains'
    | 'greater_than'
    | 'less_than'
    | 'is_empty'
    | 'is_not_empty';
  value: WorkflowValue;
  logic?: 'AND' | 'OR';
}

// Entity data interfaces for workflow processing
export interface ContactEntityData {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  organization_id?: string;
  job_title?: string;
  phone?: string;
}

export interface OrganizationEntityData {
  id: string;
  name: string;
  industry?: string;
  employee_count?: number;
  annual_revenue?: number;
  tier?: string;
  status?: string;
}

export interface OpportunityEntityData {
  id: string;
  name: string;
  organization_id?: string;
  contact_id?: string;
  stage: string;
  value: number;
  probability: number;
}

export type EntityData = ContactEntityData | OrganizationEntityData | OpportunityEntityData;

export interface WorkflowActionParameters {
  // Task creation parameters
  title?: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  due_date?: string;
  due_days?: number;
  assigned_to?: string;

  // Email parameters
  to?: string;
  subject?: string;
  body?: string;
  template_id?: string;

  // Field update parameters
  field_name?: string;
  field_value?: WorkflowValue;

  // Assignment parameters
  user_id?: string;

  // Activity parameters
  activity_type?: string;
  activity_date?: string;

  // Opportunity parameters
  opportunity_name?: string;
  opportunity_stage?: string;
  opportunity_value?: number;

  // Tag parameters
  tag_name?: string;
  tag_category?: string;
}

export interface WorkflowAction {
  type:
    | 'create_task'
    | 'send_email'
    | 'assign_to_user'
    | 'update_field'
    | 'create_activity'
    | 'create_opportunity'
    | 'move_stage'
    | 'add_tag';
  parameters: WorkflowActionParameters;
  delay_minutes?: number;
}

export interface WorkflowExecution {
  id: string;
  workflow_rule_id: string;
  entity_type: string;
  entity_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  triggered_at: string;
  completed_at?: string;
  error_message?: string;
  actions_executed: number;
  total_actions: number;
}

class WorkflowAutomationService {
  // Workflow Rule Management
  async createWorkflowRule(
    rule: Omit<WorkflowRule, 'id' | 'created_at' | 'execution_count'>
  ): Promise<WorkflowRule> {
    const { data, error } = await supabase
      .from('workflow_rules')
      .insert({
        ...rule,
        execution_count: 0,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getWorkflowRules(): Promise<WorkflowRule[]> {
    const { data, error } = await supabase
      .from('workflow_rules')
      .select('*')
      .order('priority', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  // Get single workflow rule
  async getWorkflowRule(id: string): Promise<WorkflowRule | null> {
    const { data, error } = await supabase.from('workflow_rules').select('*').eq('id', id).single();

    if (error) {
      logError('Error fetching workflow rule:', error);
      return null;
    }

    return data;
  }

  // Execute workflow in test mode
  async executeWorkflow(
    ruleId: string,
    triggerData: any = {},
    testMode: boolean = false
  ): Promise<void> {
    try {
      const rule = await this.getWorkflowRule(ruleId);
      if (!rule) throw new Error('Workflow not found');

      // Create execution record
      const { data: execution } = await supabase
        .from('workflow_executions')
        .insert({
          workflow_rule_id: rule.id,
          entity_type: 'manual',
          entity_id: 'test',
          status: 'running',
          triggered_at: new Date().toISOString(),
          test_mode: testMode,
          actions_executed: 0,
          total_actions: rule.actions.length,
        })
        .select()
        .single();

      if (!execution) throw new Error('Failed to create execution record');

      // In test mode, just simulate execution
      if (testMode) {
        // Simulate processing time
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Update execution as completed
        await supabase
          .from('workflow_executions')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
            actions_executed: rule.actions.length,
          })
          .eq('id', execution.id);

        // Update workflow last triggered
        await supabase
          .from('workflow_rules')
          .update({
            last_triggered: new Date().toISOString(),
            execution_count: (rule.execution_count || 0) + 1,
          })
          .eq('id', rule.id);

        return;
      }

      // Execute actions for real in live mode
      let actionsExecuted = 0;
      for (const action of rule.actions) {
        try {
          await this.executeAction(action, 'manual', 'live', triggerData);
          actionsExecuted++;
        } catch (error) {
          logError('Error executing action:', error);
        }
      }

      // Update execution status
      await supabase
        .from('workflow_executions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          actions_executed: actionsExecuted,
        })
        .eq('id', execution.id);

      // Update workflow
      await supabase
        .from('workflow_rules')
        .update({
          last_triggered: new Date().toISOString(),
          execution_count: (rule.execution_count || 0) + 1,
        })
        .eq('id', rule.id);
    } catch (error) {
      logError('Error executing workflow:', error);
      throw error;
    }
  }

  async updateWorkflowRule(id: string, updates: Partial<WorkflowRule>): Promise<WorkflowRule> {
    const { data, error } = await supabase
      .from('workflow_rules')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteWorkflowRule(id: string): Promise<void> {
    const { error } = await supabase.from('workflow_rules').delete().eq('id', id);

    if (error) throw error;
  }

  // Workflow Execution
  async triggerWorkflow(
    triggerType: WorkflowRule['trigger_type'],
    entityType: string,
    entityId: string,
    entityData: EntityData
  ): Promise<void> {
    try {
      // Get all active workflow rules for this trigger type
      const { data: rules } = await supabase
        .from('workflow_rules')
        .select('*')
        .eq('trigger_type', triggerType)
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (!rules?.length) return;

      // Process each rule
      for (const rule of rules) {
        if (await this.evaluateConditions(rule.conditions, entityData)) {
          await this.executeWorkflowInternal(rule, entityType, entityId, entityData);
        }
      }
    } catch (error) {
      logError('Error triggering workflow:', error);
    }
  }

  private async evaluateConditions(
    conditions: WorkflowCondition[],
    entityData: any
  ): Promise<boolean> {
    if (!conditions.length) return true;

    let result = true;
    let currentLogic: 'AND' | 'OR' = 'AND';

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];
      const conditionResult = this.evaluateCondition(condition, entityData);

      if (i === 0) {
        result = conditionResult;
      } else {
        if (currentLogic === 'AND') {
          result = result && conditionResult;
        } else {
          result = result || conditionResult;
        }
      }

      currentLogic = condition.logic || 'AND';
    }

    return result;
  }

  private evaluateCondition(condition: WorkflowCondition, entityData: EntityData): boolean {
    const fieldValue = this.getNestedValue(entityData, condition.field);

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'not_equals':
        return fieldValue !== condition.value;
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(condition.value).toLowerCase());
      case 'not_contains':
        return !String(fieldValue).toLowerCase().includes(String(condition.value).toLowerCase());
      case 'greater_than':
        return Number(fieldValue) > Number(condition.value);
      case 'less_than':
        return Number(fieldValue) < Number(condition.value);
      case 'is_empty':
        return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;
      case 'is_not_empty':
        return fieldValue && fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
      default:
        return false;
    }
  }

  private getNestedValue(obj: EntityData, path: string): WorkflowValue {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  private async executeWorkflowInternal(
    rule: WorkflowRule,
    entityType: string,
    entityId: string,
    entityData: EntityData
  ): Promise<void> {
    try {
      // If this workflow uses n8n, delegate to n8n
      if (rule.use_n8n && rule.n8n_workflow_id) {
        await this.executeN8nWorkflow(rule, entityType, entityId, entityData);
        return;
      }

      // Create workflow execution record
      const { data: execution } = await supabase
        .from('workflow_executions')
        .insert({
          workflow_rule_id: rule.id,
          entity_type: entityType,
          entity_id: entityId,
          status: 'running',
          triggered_at: new Date().toISOString(),
          actions_executed: 0,
          total_actions: rule.actions.length,
        })
        .select()
        .single();

      if (!execution) throw new Error('Failed to create workflow execution');

      // Execute actions
      let actionsExecuted = 0;
      for (const action of rule.actions) {
        try {
          if (action.delay_minutes && action.delay_minutes > 0) {
            // Schedule delayed action
            await this.scheduleDelayedAction(
              execution.id,
              action,
              entityType,
              entityId,
              entityData
            );
          } else {
            // Execute action immediately
            await this.executeAction(action, entityType, entityId, entityData);
            actionsExecuted++;
          }
        } catch (actionError) {
          logError('Error executing workflow action:', actionError);
        }
      }

      // Update execution status
      await supabase
        .from('workflow_executions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          actions_executed: actionsExecuted,
        })
        .eq('id', execution.id);

      // Update rule execution count
      await supabase
        .from('workflow_rules')
        .update({
          execution_count: rule.execution_count + 1,
          last_triggered: new Date().toISOString(),
        })
        .eq('id', rule.id);
    } catch (error) {
      logError('Error executing workflow:', error);
    }
  }

  private async executeAction(
    action: WorkflowAction,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    switch (action.type) {
      case 'create_task':
        await this.createTaskAction(action.parameters, entityType, entityId, entityData);
        break;
      case 'send_email':
        await this.sendEmailAction(action.parameters, entityType, entityId, entityData);
        break;
      case 'assign_to_user':
        await this.assignToUserAction(action.parameters, entityType, entityId);
        break;
      case 'update_field':
        await this.updateFieldAction(action.parameters, entityType, entityId);
        break;
      case 'create_activity':
        await this.createActivityAction(action.parameters, entityType, entityId, entityData);
        break;
      case 'create_opportunity':
        await this.createOpportunityAction(action.parameters, entityType, entityId, entityData);
        break;
      case 'move_stage':
        await this.moveStageAction(action.parameters, entityType, entityId);
        break;
      case 'add_tag':
        await this.addTagAction(action.parameters, entityType, entityId);
        break;
    }
  }

  private async createTaskAction(
    params: any,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    const taskData = {
      title: this.replaceTokens(params.title, entityData),
      description: this.replaceTokens(params.description || '', entityData),
      priority: params.priority || 'medium',
      due_date: params.due_date || this.calculateDueDate(params.due_days || 7),
      assigned_to: params.assigned_to,
      entity_type: entityType,
      entity_id: entityId,
      status: 'pending',
    };

    await supabase.from('tasks').insert(taskData);
  }

  private async sendEmailAction(
    params: any,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    // This would integrate with email service
    const emailData = {
      to: this.replaceTokens(params.to, entityData),
      subject: this.replaceTokens(params.subject, entityData),
      body: this.replaceTokens(params.body, entityData),
      template_id: params.template_id,
      entity_type: entityType,
      entity_id: entityId,
    };

    // For now, log the email (would integrate with actual email service)
    // Store in communication log
    await supabase.from('communications').insert({
      entity_type: entityType,
      entity_id: entityId,
      type: 'email',
      subject: emailData.subject,
      content: emailData.body,
      status: 'sent',
      automated: true,
    });
  }

  private async assignToUserAction(
    params: any,
    entityType: string,
    entityId: string
  ): Promise<void> {
    const updateData: any = {
      assigned_to: params.user_id,
      updated_at: new Date().toISOString(),
    };

    const tableName = this.getTableName(entityType);
    await supabase.from(tableName).update(updateData).eq('id', entityId);
  }

  private async updateFieldAction(
    params: any,
    entityType: string,
    entityId: string
  ): Promise<void> {
    const updateData: any = {
      [params.field]: params.value,
      updated_at: new Date().toISOString(),
    };

    const tableName = this.getTableName(entityType);
    await supabase.from(tableName).update(updateData).eq('id', entityId);
  }

  private async createActivityAction(
    params: any,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    const activityData = {
      type: params.activity_type || 'note',
      description: this.replaceTokens(params.description, entityData),
      entity_type: entityType,
      entity_id: entityId,
      automated: true,
      completed: params.auto_complete || false,
    };

    await supabase.from('activities').insert(activityData);
  }

  private async createOpportunityAction(
    params: any,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    const opportunityData: any = {
      title: this.replaceTokens(params.title, entityData),
      description: this.replaceTokens(params.description || '', entityData),
      value: params.value || 0,
      stage: params.stage || 'Lead',
      probability: params.probability || 10,
    };

    // Set organization/contact based on entity type
    if (entityType === 'contact') {
      opportunityData.contact_id = entityId;
      if (entityData.organization_id) {
        opportunityData.organization_id = entityData.organization_id;
      }
    } else if (entityType === 'organization') {
      opportunityData.organization_id = entityId;
    }

    await supabase.from('opportunities').insert(opportunityData);
  }

  private async moveStageAction(params: any, entityType: string, entityId: string): Promise<void> {
    if (entityType === 'opportunity') {
      await supabase
        .from('opportunities')
        .update({
          stage: params.stage,
          updated_at: new Date().toISOString(),
        })
        .eq('id', entityId);
    }
  }

  private async addTagAction(params: any, entityType: string, entityId: string): Promise<void> {
    // This would add tags to entities (implementation depends on tag system)
  }

  private async scheduleDelayedAction(
    executionId: string,
    action: WorkflowAction,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    const executeAt = new Date(Date.now() + action.delay_minutes! * 60 * 1000);

    await supabase.from('delayed_workflow_actions').insert({
      workflow_execution_id: executionId,
      action_type: action.type,
      action_parameters: action.parameters,
      entity_type: entityType,
      entity_id: entityId,
      execute_at: executeAt.toISOString(),
      status: 'scheduled',
    });
  }

  private replaceTokens(text: string, entityData: any): string {
    if (!text) return '';

    let result = text;

    // Replace common tokens
    const tokens: Record<string, any> = {
      '{{first_name}}': entityData.first_name || '',
      '{{last_name}}': entityData.last_name || '',
      '{{full_name}}': `${entityData.first_name || ''} ${entityData.last_name || ''}`.trim(),
      '{{email}}': entityData.email || '',
      '{{organization_name}}': entityData.organization?.name || entityData.name || '',
      '{{job_title}}': entityData.job_title || '',
      '{{phone}}': entityData.phone || '',
      '{{today}}': new Date().toLocaleDateString(),
      '{{time}}': new Date().toLocaleTimeString(),
    };

    Object.entries(tokens).forEach(([token, value]) => {
      result = result.replace(new RegExp(token, 'g'), String(value));
    });

    return result;
  }

  private calculateDueDate(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString();
  }

  private getTableName(entityType: string): string {
    const tableMap: Record<string, string> = {
      contact: 'contacts',
      organization: 'organizations',
      opportunity: 'opportunities',
      task: 'tasks',
      activity: 'activities',
    };
    return tableMap[entityType] || entityType;
  }

  // Lead Routing Automation
  async createLeadRoutingRule(rule: {
    name: string;
    conditions: WorkflowCondition[];
    assignment_type: 'round_robin' | 'load_balanced' | 'territory' | 'skill_based';
    assignment_parameters: Record<string, any>;
    is_active: boolean;
  }): Promise<any> {
    const { data, error } = await supabase
      .from('lead_routing_rules')
      .insert(rule)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async routeLead(leadData: any): Promise<string | null> {
    try {
      // Get active routing rules
      const { data: rules } = await supabase
        .from('lead_routing_rules')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (!rules?.length) return null;

      // Find matching rule
      for (const rule of rules) {
        if (await this.evaluateConditions(rule.conditions, leadData)) {
          return await this.assignByRule(rule, leadData);
        }
      }

      return null;
    } catch (error) {
      logError('Error routing lead:', error);
      return null;
    }
  }

  private async assignByRule(rule: any, leadData: any): Promise<string | null> {
    switch (rule.assignment_type) {
      case 'round_robin':
        return await this.assignRoundRobin(rule.assignment_parameters);
      case 'load_balanced':
        return await this.assignLoadBalanced(rule.assignment_parameters);
      case 'territory':
        return await this.assignByTerritory(rule.assignment_parameters, leadData);
      case 'skill_based':
        return await this.assignBySkill(rule.assignment_parameters, leadData);
      default:
        return null;
    }
  }

  private async assignRoundRobin(params: any): Promise<string | null> {
    const userIds = params.user_ids || [];
    if (!userIds.length) return null;

    // Get last assignment index
    const { data: lastAssignment } = await supabase
      .from('lead_assignments')
      .select('assigned_to, created_at')
      .in('assigned_to', userIds)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    let nextIndex = 0;
    if (lastAssignment) {
      const currentIndex = userIds.indexOf(lastAssignment.assigned_to);
      nextIndex = (currentIndex + 1) % userIds.length;
    }

    return userIds[nextIndex];
  }

  private async assignLoadBalanced(params: any): Promise<string | null> {
    const userIds = params.user_ids || [];
    if (!userIds.length) return null;

    // Get current workload for each user
    const { data: workloads } = await supabase
      .from('tasks')
      .select('assigned_to')
      .in('assigned_to', userIds)
      .eq('status', 'pending');

    const workloadCounts = userIds.reduce((acc: Record<string, number>, userId: string) => {
      acc[userId] = workloads?.filter((w) => w.assigned_to === userId).length || 0;
      return acc;
    }, {});

    // Assign to user with lowest workload
    return Object.entries(workloadCounts).reduce((min, [userId, count]) =>
      count < workloadCounts[min] ? userId : min
    );
  }

  private async assignByTerritory(params: any, leadData: any): Promise<string | null> {
    // Implementation would depend on territory mapping
    return params.default_user_id || null;
  }

  private async assignBySkill(params: any, leadData: any): Promise<string | null> {
    // Implementation would depend on skill matching
    return params.default_user_id || null;
  }

  // Process Delayed Actions
  async processDelayedActions(): Promise<void> {
    try {
      const now = new Date().toISOString();

      const { data: delayedActions } = await supabase
        .from('delayed_workflow_actions')
        .select('*')
        .eq('status', 'scheduled')
        .lte('execute_at', now)
        .limit(10);

      if (!delayedActions?.length) return;

      for (const delayedAction of delayedActions) {
        try {
          // Mark as running
          await supabase
            .from('delayed_workflow_actions')
            .update({ status: 'running' })
            .eq('id', delayedAction.id);

          // Get entity data
          const tableName = this.getTableName(delayedAction.entity_type);
          const { data: entityData } = await supabase
            .from(tableName)
            .select('*')
            .eq('id', delayedAction.entity_id)
            .single();

          if (entityData) {
            // Execute the action
            const action: WorkflowAction = {
              type: delayedAction.action_type,
              parameters: delayedAction.action_parameters,
            };

            await this.executeAction(
              action,
              delayedAction.entity_type,
              delayedAction.entity_id,
              entityData
            );

            // Mark as completed
            await supabase
              .from('delayed_workflow_actions')
              .update({
                status: 'completed',
                executed_at: new Date().toISOString(),
              })
              .eq('id', delayedAction.id);
          } else {
            // Entity no longer exists, mark as failed
            await supabase
              .from('delayed_workflow_actions')
              .update({
                status: 'failed',
                error_message: 'Entity not found',
              })
              .eq('id', delayedAction.id);
          }
        } catch (error) {
          logError('Error executing delayed action:', error);
          await supabase
            .from('delayed_workflow_actions')
            .update({
              status: 'failed',
              error_message: error instanceof Error ? error.message : 'Unknown error',
            })
            .eq('id', delayedAction.id);
        }
      }
    } catch (error) {
      logError('Error processing delayed actions:', error);
    }
  }

  // Analytics and Reporting
  async getWorkflowAnalytics(timeframe: 'day' | 'week' | 'month' = 'week'): Promise<any> {
    const startDate = new Date();
    switch (timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
    }

    const [executions, rules] = await Promise.all([
      supabase.from('workflow_executions').select('*').gte('triggered_at', startDate.toISOString()),
      supabase
        .from('workflow_rules')
        .select('id, name, execution_count, last_triggered')
        .eq('is_active', true),
    ]);

    const executionData = executions.data || [];
    const ruleData = rules.data || [];

    return {
      total_executions: executionData.length,
      successful_executions: executionData.filter((e) => e.status === 'completed').length,
      failed_executions: executionData.filter((e) => e.status === 'failed').length,
      avg_execution_time: this.calculateAverageExecutionTime(executionData),
      most_active_rules: ruleData.sort((a, b) => b.execution_count - a.execution_count).slice(0, 5),
      execution_by_day: this.groupExecutionsByDay(executionData),
    };
  }

  private calculateAverageExecutionTime(executions: any[]): number {
    const completedExecutions = executions.filter(
      (e) => e.status === 'completed' && e.completed_at
    );
    if (!completedExecutions.length) return 0;

    const totalTime = completedExecutions.reduce((sum, exec) => {
      const start = new Date(exec.triggered_at).getTime();
      const end = new Date(exec.completed_at).getTime();
      return sum + (end - start);
    }, 0);

    return Math.round(totalTime / completedExecutions.length / 1000); // Return in seconds
  }

  private groupExecutionsByDay(executions: any[]): Record<string, number> {
    return executions.reduce((acc, exec) => {
      const day = exec.triggered_at.split('T')[0];
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});
  }

  // n8n Integration Methods
  private async executeN8nWorkflow(
    rule: WorkflowRule,
    entityType: string,
    entityId: string,
    entityData: any
  ): Promise<void> {
    try {
      // Trigger n8n workflow
      await n8nService.triggerWorkflow(rule.n8n_workflow_id!, rule.trigger_type, {
        entityType,
        entityId,
        entityData,
        ruleName: rule.name,
        conditions: rule.conditions,
        metadata: {
          triggered_by: 'spherical_nexus_crm',
          timestamp: new Date().toISOString(),
        },
      });

      // Update rule execution count
      await supabase
        .from('workflow_rules')
        .update({
          execution_count: rule.execution_count + 1,
          last_triggered: new Date().toISOString(),
        })
        .eq('id', rule.id);
    } catch (error) {
      logError('Error executing n8n workflow:', error);
      throw error;
    }
  }

  async createN8nWorkflow(
    rule: Omit<WorkflowRule, 'id' | 'created_at' | 'execution_count'>,
    workflowType: string
  ): Promise<WorkflowRule> {
    try {
      // Create n8n workflow based on type
      const n8nWorkflow = await n8nService.createCRMWorkflow(workflowType, {
        name: rule.name,
        conditions: rule.conditions,
        actions: rule.actions,
        triggerType: rule.trigger_type,
      });

      // Create workflow rule with n8n ID
      const workflowRule = await this.createWorkflowRule({
        ...rule,
        n8n_workflow_id: n8nWorkflow.id,
        use_n8n: true,
      });

      return workflowRule;
    } catch (error) {
      logError('Error creating n8n workflow:', error);
      throw error;
    }
  }

  async syncWithN8n(ruleId: string): Promise<void> {
    try {
      const { data: rule } = await supabase
        .from('workflow_rules')
        .select('*')
        .eq('id', ruleId)
        .single();

      if (!rule) throw new Error('Workflow rule not found');

      if (rule.n8n_workflow_id) {
        // Update existing n8n workflow
        await n8nService.updateWorkflow(rule.n8n_workflow_id, {
          name: rule.name,
          active: rule.is_active,
        });
      } else {
        // Create new n8n workflow
        const n8nWorkflow = await n8nService.createCRMWorkflow(rule.trigger_type, {
          name: rule.name,
          conditions: rule.conditions,
          actions: rule.actions,
        });

        // Update rule with n8n ID
        await supabase
          .from('workflow_rules')
          .update({
            n8n_workflow_id: n8nWorkflow.id,
            use_n8n: true,
          })
          .eq('id', ruleId);
      }
    } catch (error) {
      logError('Error syncing with n8n:', error);
      throw error;
    }
  }

  async getN8nExecutions(workflowId: string): Promise<any[]> {
    try {
      const { data: rule } = await supabase
        .from('workflow_rules')
        .select('n8n_workflow_id')
        .eq('id', workflowId)
        .single();

      if (!rule?.n8n_workflow_id) return [];

      const executions = await n8nService.getExecutions(rule.n8n_workflow_id);
      return executions;
    } catch (error) {
      logError('Error getting n8n executions:', error);
      return [];
    }
  }
}

export const workflowAutomationService = new WorkflowAutomationService();
